import firebase from "firebase/app";
import "firebase/firestore";

import { normalizarUsuario, parseFechaFlexible } from "@/services/usuarios";
import { cargarIndiceSuscripciones } from "@/services/suscripciones";
import * as metricas from "@/services/metricas";

/**
 * Fuente única de datos para las vistas analíticas.
 *
 * Antes cada vista leía Firestore y el índice de Stripe por su cuenta
 * (`default.vue`, `reconstructores/index.vue` y `plus/index.vue` tenían la
 * misma petición copiada), así que navegar entre ellas repetía el trabajo y las
 * cifras podían no coincidir entre pantallas.
 *
 * La caché es en memoria: sobrevive a los cambios de ruta, no a un recargado
 * completo del navegador. No se guarda en storage porque el padrón puede pesar
 * varios MB y no cabe de forma confiable.
 */

const TTL_MS = 5 * 60 * 1000;

export const state = {
  usuarios: [],
  negocios: [],
  sesiones: [],
  vacantes: [],
  eventos: [],
  indiceSuscripciones: {},
  cargando: false,
  cargadoEn: null,
  error: null,
};

export const mutations = {
  SET_CARGANDO(state, valor) {
    state.cargando = valor;
  },
  SET_ERROR(state, valor) {
    state.error = valor;
  },
  SET_DATOS(state, { usuarios, negocios, sesiones, vacantes, eventos, indiceSuscripciones }) {
    state.usuarios = usuarios;
    state.negocios = negocios;
    state.sesiones = sesiones;
    state.vacantes = vacantes;
    state.eventos = eventos;
    state.indiceSuscripciones = indiceSuscripciones;
    state.cargadoEn = Date.now();
  },
  ACTUALIZAR_USUARIO(state, { id, cambios }) {
    const i = state.usuarios.findIndex((u) => u.id === id);
    if (i === -1) return;
    state.usuarios.splice(i, 1, normalizarUsuario({ ...state.usuarios[i], ...cambios }));
  },
};

export const getters = {
  listo: (state) => !!state.cargadoEn,

  /** Índice código de referidor → referidos. */
  indiceReferidos: (state) => metricas.construirIndiceReferidos(state.usuarios),

  /** Usuarios con su estado de membresía ya resuelto. */
  usuariosConMembresia: (state) =>
    state.usuarios.map((u) => ({
      ...u,
      membresia: metricas.membresiaDe(u, state.indiceSuscripciones),
    })),

  /** KPIs para un rango dado: `getters['analytics/resumen'](rango)`. */
  resumen: (state) => (rango) =>
    metricas.calcularResumen(
      {
        usuarios: state.usuarios,
        negocios: state.negocios,
        sesiones: state.sesiones,
        vacantes: state.vacantes,
        eventos: state.eventos,
        indice: state.indiceSuscripciones,
      },
      rango
    ),

  serieRegistros: (state) => (rango, granularidad) =>
    metricas.serieRegistros(state.usuarios, rango, granularidad),

  embudo: (state) => (rango) =>
    metricas.calcularEmbudo(state.usuarios, state.indiceSuscripciones, rango),

  topReferidores: (state) => (limite) =>
    metricas.topReferidores(state.usuarios, state.indiceSuscripciones, limite),

  porVencer: (state) => (dias) =>
    metricas.suscripcionesPorVencer(state.usuarios, state.indiceSuscripciones, dias),

  estancados: (state) => (dias) => metricas.registrosEstancados(state.usuarios, dias),
};

export const actions = {
  /**
   * Carga todo lo necesario para el tablero. Respeta la caché salvo que se
   * pida `force`, para que moverse entre vistas no vuelva a leer Firestore.
   */
  async cargar({ commit, state }, { force = false } = {}) {
    if (state.cargando) return;
    if (!force && state.cargadoEn && Date.now() - state.cargadoEn < TTL_MS) return;

    commit("SET_CARGANDO", true);
    commit("SET_ERROR", null);

    try {
      const [usuariosDocs, negocios, sesiones, vacantes, eventos, indiceSuscripciones] =
        await Promise.all([
          leerColeccion("app_usuarios"),
          leerColeccion("negocios"),
          leerColeccion("sesiones"),
          leerColeccion("vacantes"),
          leerColeccion("queplanhoy"),
          cargarIndiceSuscripciones().catch((err) => {
            // Sin Stripe seguimos mostrando registros: es preferible un tablero
            // parcial a uno vacío.
            console.warn("[analytics] no se pudo cargar el índice de Stripe:", err);
            return {};
          }),
        ]);

      commit("SET_DATOS", {
        usuarios: normalizarYDeduplicar(usuariosDocs),
        negocios: negocios.map((n) => ({
          ...n,
          registradoEn: parseFechaFlexible(n.register_date),
          giro: n.giro || "Sin giro",
        })),
        sesiones,
        vacantes,
        eventos,
        indiceSuscripciones,
      });
    } catch (err) {
      console.error("[analytics] error al cargar:", err);
      commit("SET_ERROR", err && err.message ? err.message : "Error al cargar los datos");
    } finally {
      commit("SET_CARGANDO", false);
    }
  },

  refrescar({ dispatch }) {
    return dispatch("cargar", { force: true });
  },
};

// ===
// Helpers privados
// ===

async function leerColeccion(nombre) {
  try {
    const snap = await firebase.firestore().collection(nombre).get();
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    // Una colección que aún no existe o sin permisos no debe tumbar el tablero.
    console.warn(`[analytics] no se pudo leer "${nombre}":`, err);
    return [];
  }
}

/**
 * La app da de alta con `.add()`, así que un mismo correo puede tener más de un
 * documento (por ejemplo un alta fallida que dejó un `signup_pending` huérfano).
 * Contarlos por separado inflaba el total de registros.
 */
function normalizarYDeduplicar(docs) {
  const porEmail = new Map();
  const sinEmail = [];

  for (const doc of docs) {
    const u = normalizarUsuario(doc);
    if (!u.email) {
      sinEmail.push(u);
      continue;
    }
    const previo = porEmail.get(u.email);
    porEmail.set(u.email, previo ? elegirMejorDocumento(previo, u) : u);
  }

  return [...porEmail.values(), ...sinEmail];
}

/** Gana el perfil completado; a igualdad de estado, el más reciente. */
function elegirMejorDocumento(a, b) {
  const completadoA = a.estadoRegistro === "completado";
  const completadoB = b.estadoRegistro === "completado";
  if (completadoA !== completadoB) return completadoA ? a : b;

  const fechaA = a.creadoEn && a.creadoEn.isValid() ? a.creadoEn.valueOf() : 0;
  const fechaB = b.creadoEn && b.creadoEn.isValid() ? b.creadoEn.valueOf() : 0;
  return fechaA >= fechaB ? a : b;
}
